import { lessonsFor, mc, tf } from "./helpers";
import type { LessonContentMeta } from "./types";

const chat = { label: "Chat with GenieBot", href: "/chat" };
const tools = { label: "All tools", href: "/tools" };

const REVIEWED = "2026-10-12";

const fluencyMeta: LessonContentMeta = {
  contentVersion: "1.0",
  author: "GenieBot Learning",
  lastReviewedAt: REVIEWED,
  nextReviewAt: "2027-01-12",
  freshness: "fast-changing",
  sources: [
    { title: "AI Competency Framework", organization: "UNESCO", url: "https://www.unesco.org/en/articles/ai-competency-framework-for-students" },
    { title: "Generative AI Profile", organization: "NIST", url: "https://www.nist.gov/itl/ai-risk-management-framework" },
  ],
};

/** AI Fluency expansion. Appended to the track in learningHub.ts. */
export const AI_FLUENCY_EXPANDED = lessonsFor("ai-fluency", [
  {
    slug: "multimodal-ai",
    title: "Multimodal AI",
    minutes: 4,
    learn: ["AI can work with text, images, audio, video and files", "Capabilities differ by model and product"],
    explanation: [
      "Multimodal AI works across kinds of input: you can show it a photo, upload a spreadsheet, or speak instead of type — and get text, images or audio back.",
      "Capabilities vary widely between models and products. One may read images but not video; another transcribes audio but can't generate it. Check what your specific tool supports before designing a task around it.",
    ],
    example: "You photograph a whiteboard after a meeting and ask for the action items as a list — the AI reads the image and writes the list.",
    check: tf("Every AI assistant can handle images, audio and video equally well.", false, "Modality support differs by model and product. Check the specific tool's capabilities."),
    takeaway: "AI isn't just text — but check which modes your tool actually supports.",
    tool: chat,
    resource: { label: "Learning Articles", href: "/blogs" },
    meta: fluencyMeta,
  },
  {
    slug: "training-vs-inference",
    title: "Training vs Inference",
    minutes: 4,
    learn: ["Training: how a model learns patterns", "Inference: when it generates an answer for you"],
    explanation: [
      "Training is the one-time (very expensive) process where a model learns patterns from huge amounts of data. After training, the model's knowledge is frozen at that point.",
      "Inference is what happens when you use it: the trained model generates an answer. Chatting with an assistant doesn't retrain it — which is why a model can be ignorant of last week's news even while sounding smart about everything older.",
    ],
    example: "A model trained with data up to 2025 can beautifully explain events from 2024 — but invents details about a 2026 product launch it never saw.",
    check: mc("single", "When you chat with an AI assistant, what are you using?", ["Training", "*Inference", "Programming"], "Using a trained model to generate answers is inference. Training happened beforehand, once."),
    takeaway: "Training teaches the model; inference is it answering you. Chatting doesn't update its knowledge.",
    tool: chat,
    resource: { label: "Learning Articles", href: "/blogs" },
    meta: fluencyMeta,
  },
  {
    slug: "memory-context-retrieval",
    title: "Memory vs Context vs Retrieval",
    minutes: 6,
    difficulty: "Intermediate",
    learn: ["Context: what's in the current conversation", "Memory: what's stored; Retrieval: what's fetched"],
    explanation: [
      "Context is everything visible in the current conversation — your messages, the AI's replies, pasted documents. When the chat ends or the context fills up, it's gone.",
      "Memory is information the system deliberately stores for later conversations (like your name or preferences). Retrieval (RAG) is when the system fetches information from external sources — documents, databases, the web — at the moment it answers. Each behaves differently: context is temporary, memory persists, retrieval brings in outside facts that still need checking.",
    ],
    example: "Your assistant remembers your dietary preferences (memory), sees the recipe you just pasted (context), and pulls current prices from a store's site (retrieval).",
    interactive: "memory-context-retrieval",
    check: mc("single", "An AI looks up today's stock price from a financial website while answering. What is that?", ["Context", "Memory", "*Retrieval"], "Fetching external information at answer time is retrieval (RAG). Context is the conversation itself; memory is stored preferences."),
    takeaway: "Context is the current chat, memory is stored, retrieval is fetched — know which one your answer depends on.",
    tool: chat,
    resource: { label: "Learning Articles", href: "/blogs" },
    meta: fluencyMeta,
  },
  {
    slug: "web-connected-ai",
    title: "Web-Connected AI",
    minutes: 5,
    learn: ["Base model knowledge has a cutoff", "Retrieved information can still be wrong"],
    explanation: [
      "A model's built-in knowledge stops at its training cutoff. Web-connected systems search or fetch current pages to answer about recent events — a real improvement for anything time-sensitive.",
      "But retrieval isn't truth: the web contains errors, outdated pages and deliberate misinformation. Check the source quality and publication date of what the AI found, just as you would with a search engine result.",
    ],
    example: "Asked about a new tax rule, the assistant searches the web and quotes a blog post from 2024. The official tax authority page — published last month — says the rule changed.",
    check: mc("multi", "What should you check when AI gives you web-retrieved facts? (Choose all that apply)", ["*The source's reliability", "*The publication date", "Nothing — web access means it's current", "*Whether official sources agree"], "Web access fixes the cutoff problem, not the credibility problem. Source quality and dates still matter."),
    takeaway: "Web-connected AI is more current, not more correct — check the sources it used.",
    tool: chat,
    resource: { label: "Learning Articles", href: "/blogs" },
    meta: fluencyMeta,
  },
  {
    slug: "choosing-the-right-model",
    title: "Choosing the Right AI Model or Tool",
    minutes: 5,
    learn: ["Tradeoffs: capability, speed, cost, privacy, context size", "Match the tool to the task type"],
    explanation: [
      "There's no universally 'best' model — there are tradeoffs. Bigger models handle harder reasoning; smaller ones are faster and cheaper. Some accept huge documents (large context); some handle images or audio; some connect to tools.",
      "Match the tool to the task: quick drafts and summaries need speed; sensitive data needs a privacy-friendly setup; complex analysis needs capability and context. And re-evaluate occasionally — capabilities change fast.",
    ],
    example: "For summarizing meeting notes you use the fast, cheap option. For reviewing a 200-page contract, you switch to the model with the large context window.",
    check: tf("The most capable model is the right choice for every task.", false, "Capability costs speed and money, and may be unnecessary. Match the tool to the task's real requirements."),
    takeaway: "Pick for the task: capability, speed, cost, privacy, context and modality are the dials.",
    tool: tools,
    resource: { label: "Learning Articles", href: "/blogs" },
    meta: fluencyMeta,
  },
  {
    slug: "ai-evaluation-uncertainty",
    title: "AI Evaluation and Uncertainty",
    minutes: 5,
    difficulty: "Intermediate",
    learn: ["Evaluate outputs on accuracy, consistency and grounding", "Confident language is not a calibrated probability"],
    explanation: [
      "When an AI answer matters, evaluate it like you'd evaluate a person's work: is it accurate? Consistent when asked twice? Grounded in sources? Safe? Did it actually complete the task?",
      "Beware fluent confidence. 'I'm certain that…' is a language style, not a measured probability — models don't know how likely they are to be right. Representative test cases beat vibes: try the prompt on several real examples and score the results.",
    ],
    example: "Before trusting an AI to categorize support tickets, you run 50 past tickets through it and count how many it gets right — 44. Now you know its real accuracy.",
    check: mc("single", "An AI says 'I'm 100% sure.' What does that tell you?", ["The answer is definitely right", "*Nothing reliable — confident wording isn't a measured probability", "It checked its sources"], "Models generate confident language by pattern, not by calculation. Test results, not tone, measure reliability."),
    takeaway: "Measure AI with test cases; never read confidence from its tone.",
    tool: tools,
    resource: { label: "Learning Articles", href: "/blogs" },
    meta: fluencyMeta,
  },
  {
    slug: "basic-ai-system-design",
    title: "Basic AI System Design",
    minutes: 7,
    difficulty: "Intermediate",
    learn: ["Combine goal, model, context, tools, verification and human approval", "Design the workflow before automating it"],
    explanation: [
      "A reliable AI workflow has parts, in order: a clear Goal; the right Model; the Context/Data it needs; any Tools it can use; Verification of its output; Human Approval for actions that matter; and only then the Output.",
      "Skipping parts is where workflows fail: no context means guessing, no verification means errors ship, no human approval means an injected instruction can take real action. This lesson is the blueprint for the AI Fluency capstone, where you'll design a full workflow yourself.",
    ],
    example: "Invoice-processing workflow: goal (categorize invoices) → model (large context) → data (the invoice PDFs) → tools (read-only folder access) → verification (totals cross-checked) → human approval (before any payment) → output (a categorized list).",
    check: mc("single", "Which part stops an AI workflow from taking a harmful action on bad input?", ["A bigger model", "*Human approval before important actions", "A longer prompt"], "Model size and prompt length don't contain actions. An approval gate does."),
    takeaway: "Goal → Model → Context → Tools → Verification → Human approval → Output. Every part earns its place.",
    tool: tools,
    resource: { label: "Learning Articles", href: "/blogs" },
    meta: fluencyMeta,
  },
]);
